import _ from 'lodash';
import { computed } from 'vue-demi';

import { ISession } from '@/types';
import { useRouter, useAuth } from '@/composables';
import { useSessionsStore } from './use-sessions-store';
import { useContacts } from '@/modules/contacts-store';
import { useSessionViews } from '@/modules/session-views-store';
import { applySessionFilter, needsActionCheck } from '@/common';
import { SESSION_STATUS } from '@/constants';

export type SessionsOrder = 'CREATED_AT' | 'EVENT_TIMESTAMP';

/**
 * Note: We apply session filter as plain computed to limit computations.
 */
export const useSessionsGetters = () => {
  const store = useSessionsStore();
  const { AppRouter, query } = useRouter();
  const { userId } = useAuth();
  const { activeContactId } = useContacts();
  const { activeConversationFilter } = useSessionViews();

  const activeSessionId = computed(() => {
    return query.value[AppRouter.QUERY_PARAMS.SESSION_ID] as string;
  });

  const activeSession = computed(() => {
    return store.sessionsById[activeSessionId.value];
  });

  /** Check if session exists and can agent in "Claimed By" array */
  const canInteractByActiveAgent = computed(() => {
    return (sessionId: string) => {
      const session = store.sessionsById[sessionId];
      if (!session) {
        return false;
      }

      return session.status === SESSION_STATUS.CLAIMED
        && session.claimedBy.includes(userId.value);
    };
  });

  const needsActionByActiveAgent = computed(() => {
    return (sessionId: string) => {
      const session = store.sessionsById[sessionId];
      if (!session || session.status === 'CLOSED') {
        return false;
      }

      const { needsAction = false, needsActionFrom = [] } = session;
      return needsActionCheck({
        needsAction,
        needsActionFrom,
        userId: userId.value,
      });
    };
  });

  /** Creation date order is used in timeline */
  const sessionsOrderByCreatedAt = computed(() => {
    const DIRECTION = 'asc';
    const getter = (i: ISession) => i.createdAt!.getTime();
    return _.orderBy(store.sessionsById, getter, [DIRECTION]);
  });

  /** Event timestamp order is used in conversations list */
  const sessionOrderByEventTimestamp = computed(() => {
    const DIRECTION = 'desc';
    const getter = (i: ISession) => {
      return store.eventTimestampsById[i.sessionId].getTime();
    };

    return _.orderBy(store.sessionsById, getter, [DIRECTION]);
  });

  const sessionsOrderByCreatedAtWF = computed(() => {
    const sessions = sessionsOrderByCreatedAt.value;
    return activeConversationFilter.value
      ? applySessionFilter(sessions, userId.value, activeConversationFilter.value)
      : sessions;
  });

  const sessionOrderByEventTimestampWF = computed(() => {
    const sessions = sessionOrderByEventTimestamp.value;
    return activeConversationFilter.value
      ? applySessionFilter(sessions, userId.value, activeConversationFilter.value)
      : sessions;
  });

  const sessionsByActiveFilterObj = computed(() => {
    return _.keyBy(sessionOrderByEventTimestampWF.value, 'sessionId');
  });

  const contactIdsByActiveFilter = computed(() => {
    return _.uniq(sessionOrderByEventTimestampWF.value.map(i => i.contactId));
  });

  /** Usually, when contact is active, all their sessions are visible in timeline */
  const sessionsInTimeline = computed(() => {
    if (!activeContactId.value) {
      return [];
    }

    return sessionOrderByEventTimestamp.value.filter(session => {
      return session.contactId === activeContactId.value;
    });
  });

  /** Default order property is "Created At" */
  const getSessionsOrderByProperty = computed(() => {
    return (options?: { order?: SessionsOrder; applyFilter?: boolean; }) => {
      const { order, applyFilter } = options || {};
      switch (order) {
        case 'CREATED_AT':
          return applyFilter
            ? sessionsOrderByCreatedAtWF.value
            : sessionsOrderByCreatedAt.value;
        case 'EVENT_TIMESTAMP':
        default:
          return applyFilter
            ? sessionOrderByEventTimestampWF.value
            : sessionOrderByEventTimestamp.value;
      }
    };
  });

  /** Apply filter options, like contact ID, to available in store sessions */
  const getSessionsWithOptions = computed(() => {
    return (options?: {
      contactId?: string;
      order?: SessionsOrder;
      applyFilter?: boolean;
    }) => {
      const { contactId, order, applyFilter = true } = options || {};
      const sessions = getSessionsOrderByProperty.value({ order, applyFilter });

      return contactId ? _.filter(sessions, { contactId }) : sessions;
    };
  });

  /**
   * Active session can be hidden by active filter.
   * If there is no active session, consider it included in results.
   */
  const isActiveSessionInFilterResults = computed(() => {
    return activeSession.value
      ? !!sessionsByActiveFilterObj.value[activeSessionId.value]
      : true;
  });

  /**
   * Only open sessions are used in "Receive New Events" feature.
   * We check sessions visible in "Conversations List" and in "Timeline".
   */
  const visibleOpenSessionIds = computed(() => {
    const ids = [];
    const sessions = sessionOrderByEventTimestampWF.value
      .concat(sessionsInTimeline.value);

    if (!isActiveSessionInFilterResults.value) {
      ids.push(activeSessionId.value);
    }

    for (const session of sessions) {
      if (session.status !== SESSION_STATUS.CLOSED) {
        ids.push(session.sessionId);
      }
    }

    return _.uniq(ids);
  });

  /**
   * Only ongoing and claimed by current agent sessions are used
   * in "Show New Events Indicators" feature.
   */
  const visibleOpenForAgentSessionIds = computed(() => {
    const ids = [];
    const sessions = sessionOrderByEventTimestampWF.value
      .concat(sessionsInTimeline.value);

    if (!isActiveSessionInFilterResults.value) {
      ids.push(activeSessionId.value);
    }

    for (const session of sessions) {
      if (session.status === SESSION_STATUS.ONGOING) {
        ids.push(session.sessionId);
      }

      if (session.status === SESSION_STATUS.CLAIMED) {
        if (session.claimedBy.includes(userId.value)) {
          ids.push(session.sessionId);
        }
      }
    }

    return _.uniq(ids);
  });

  return {
    activeSessionId,
    activeSession,
    canInteractByActiveAgent,
    needsActionByActiveAgent,
    sessionsOrderByCreatedAt,
    sessionOrderByEventTimestamp,
    sessionsOrderByCreatedAtWF,
    sessionOrderByEventTimestampWF,
    sessionsInTimeline,
    getSessionsOrderByProperty,
    getSessionsWithOptions,
    sessionsByActiveFilterObj,
    contactIdsByActiveFilter,
    isActiveSessionInFilterResults,
    visibleOpenSessionIds,
    visibleOpenForAgentSessionIds,
  };
};
